import React, { useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileUp, Loader2, Upload, X, XCircle } from 'lucide-react';
import { Approach, CycleFrequency } from '../types';
import { APPROACH_LIST, INDICATORS } from '../constants';
import { createPatient } from '../services/patientsApi';
import { describeError } from '../utils/describeError';
import { isProfessionalRole } from '../utils/patientAccess';
import {
  autoMap,
  buildImportRows,
  IMPORT_FIELD_LABELS,
  IMPORT_FIELDS,
  parseCsv,
  summarizeImport,
  type ColumnMapping,
  type ExistingPatient,
  type ImportField,
  type ImportRow,
  type ParsedCsv,
} from '../utils/csvImport';

type Result = { line: number; name: string; ok: boolean; message: string };

/**
 * Bloco #63: "Importar dados" em Configurações — CSV de outro software →
 * cadastro de pacientes do Allodos, com prévia, mapeamento de colunas,
 * sinalização de duplicidades e relatório final. Nada é gravado antes do
 * clique em "Confirmar importação".
 */
export const ImportData: React.FC<{
  patients: ExistingPatient[];
  professionals: { id: string; name: string }[];
  activeProfile: { id: string; name?: string; role?: string };
  onImported?: () => void | Promise<void>;
}> = ({ patients, professionals, activeProfile, onImported }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [fileError, setFileError] = useState<string | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [approach, setApproach] = useState<string>(Approach.TCC);
  const [frequency, setFrequency] = useState<string>(CycleFrequency.MENSAL);
  const lockedToSelf = isProfessionalRole(activeProfile.role);
  const [professionalId, setProfessionalId] = useState<string>(lockedToSelf ? String(activeProfile.id) : '');
  // A API exige ao menos um indicador: começa com o primeiro de cada domínio.
  const [indicatorIds, setIndicatorIds] = useState<string[]>(() => {
    const seen = new Set<string>();
    return INDICATORS.filter((i) => (seen.has(i.dominio) ? false : (seen.add(i.dominio), true))).map((i) => i.id);
  });
  const toggleIndicator = (id: string) => setIndicatorIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  const indicatorsByDomain = useMemo(() => {
    const groups = new Map<string, typeof INDICATORS>();
    INDICATORS.forEach((i) => groups.set(i.dominio, [...(groups.get(i.dominio) ?? []), i]));
    return Array.from(groups.entries());
  }, []);
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<Result[] | null>(null);

  const rows = useMemo<ImportRow[]>(() => (parsed ? buildImportRows(parsed, mapping, patients) : []), [parsed, mapping, patients]);
  const summary = useMemo(() => (parsed ? summarizeImport(rows, parsed.headers, mapping) : null), [parsed, rows, mapping]);
  const hasNameColumn = Object.values(mapping).includes('name');
  const toImport = useMemo(
    () => rows.filter((r) => r.errors.length === 0 && !(skipDuplicates && (r.duplicateOf || r.duplicateInFile !== undefined))),
    [rows, skipDuplicates],
  );

  const reset = () => {
    setParsed(null);
    setMapping({});
    setFileName('');
    setFileError(null);
    setConfirming(false);
    setResults(null);
    setProgress(0);
    if (fileRef.current) fileRef.current.value = '';
  };

  const onFile = async (file: File | undefined) => {
    reset();
    if (!file) return;
    setFileName(file.name);
    if (!/\.(csv|txt)$/i.test(file.name)) {
      setFileError('Selecione um arquivo .csv exportado do software de origem.');
      return;
    }
    try {
      const text = await file.text();
      const p = parseCsv(text);
      if (!p.headers.length || !p.rows.length) {
        setFileError('O arquivo está vazio ou não tem linha de cabeçalho com os nomes das colunas.');
        return;
      }
      setParsed(p);
      setMapping(autoMap(p.headers));
    } catch (err) {
      setFileError(`Não foi possível ler o arquivo: ${describeError(err)}`);
    }
  };

  const changeMapping = (col: number, field: ImportField | '') => {
    setMapping((prev) => {
      const next: ColumnMapping = { ...prev };
      // Um campo só pode vir de uma coluna: tira a atribuição anterior.
      if (field) Object.keys(next).forEach((k) => { if (next[Number(k)] === field) next[Number(k)] = null; });
      next[col] = field || null;
      return next;
    });
  };

  const run = async () => {
    setRunning(true);
    setProgress(0);
    const out: Result[] = [];
    for (const row of toImport) {
      try {
        await createPatient({
          name: row.record.name,
          dob: row.record.dob,
          phone: row.record.phone,
          email: row.record.email,
          cpf: row.record.cpf,
          cep: row.record.cep,
          address: row.record.address,
          emergencyContact: row.record.emergencyContact,
          emergencyPhone: row.record.emergencyPhone,
          motherName: row.record.motherName,
          fatherName: row.record.fatherName,
          mainComplaint: row.record.mainComplaint,
          medications: row.record.medications,
          activeApproach: approach,
          cycleFrequency: frequency,
          selectedIndicatorIds: indicatorIds,
          // Profissional: sempre o próprio usuário; nunca o nome vindo do CSV.
          assignedProfessionalId: lockedToSelf ? String(activeProfile.id) : professionalId || undefined,
        });
        out.push({ line: row.line, name: row.record.name, ok: true, message: 'Importado' });
      } catch (err) {
        out.push({ line: row.line, name: row.record.name, ok: false, message: describeError(err) });
      }
      setProgress(out.length);
    }
    // Linhas que ficaram de fora entram no relatório com o motivo.
    rows.forEach((r) => {
      if (toImport.includes(r)) return;
      const reason = r.errors.length
        ? r.errors.join(' ')
        : r.duplicateOf
          ? `Possível duplicidade com ${r.duplicateOf.name} (${r.duplicateOf.reason}) — não importado.`
          : `Repetido na linha ${r.duplicateInFile} do arquivo — não importado.`;
      out.push({ line: r.line, name: r.record.name || '(sem nome)', ok: false, message: reason });
    });
    out.sort((a, b) => a.line - b.line);
    setResults(out);
    setRunning(false);
    setConfirming(false);
    if (out.some((r) => r.ok)) await onImported?.();
  };

  const select = 'w-full p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-[11px] font-bold text-slate-700 dark:text-slate-200 outline-none';
  const label = 'text-[8px] font-black uppercase tracking-widest text-slate-400 block ml-1 mb-1';
  const previewRows = rows.slice(0, 8);
  const imported = results?.filter((r) => r.ok).length ?? 0;
  const failed = results ? results.length - imported : 0;

  return (
    <section className="space-y-8">
      <div className="flex items-center gap-3">
        <FileUp className="text-indigo-500" size={20} />
        <h4 className="text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.3em]">Importar dados</h4>
      </div>
      <div className="bg-slate-50 dark:bg-slate-800/50 p-5 sm:p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-700 space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-sm font-black text-slate-800 dark:text-white">Migrar pacientes de outro software (CSV)</p>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium mt-1 max-w-xl leading-relaxed">
              Envie o CSV exportado do sistema anterior. Você confere a prévia, associa as colunas aos campos do Allodos e só então confirma.
              Campos suportados: {IMPORT_FIELDS.map((f) => IMPORT_FIELD_LABELS[f].replace(' (obrigatório)', '')).join(', ')}.
              {lockedToSelf && ' Os pacientes importados ficam vinculados a você.'}
            </p>
          </div>
          <label className="shrink-0 cursor-pointer px-6 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/10">
            <Upload size={14} /> Selecionar arquivo CSV
            <input ref={fileRef} type="file" accept=".csv,text/csv,.txt" className="sr-only" onChange={(e) => void onFile(e.target.files?.[0])} data-testid="import-csv-input" />
          </label>
        </div>

        {fileError && (
          <div role="alert" className="flex gap-3 p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 text-xs font-bold">
            <XCircle size={18} className="shrink-0" /> {fileError}
          </div>
        )}

        {results && (
          <div className="space-y-3" data-testid="import-result">
            <div className={`flex gap-3 p-4 rounded-2xl text-xs font-bold ${failed ? 'bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200' : 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200'}`}>
              <CheckCircle2 size={18} className="shrink-0" />
              <span>Importação concluída: {imported} registro(s) importado(s), {failed} não importado(s).</span>
            </div>
            {failed > 0 && (
              <div className="max-h-56 overflow-y-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                <table className="w-full text-left text-[11px]">
                  <thead className="bg-slate-50 dark:bg-slate-800 text-[9px] font-black uppercase tracking-widest text-slate-400"><tr><th className="px-4 py-2">Linha</th><th className="px-4 py-2">Nome</th><th className="px-4 py-2">Motivo</th></tr></thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {results.filter((r) => !r.ok).map((r) => (
                      <tr key={r.line}><td className="px-4 py-2 font-bold text-slate-500">{r.line}</td><td className="px-4 py-2 font-bold dark:text-white">{r.name}</td><td className="px-4 py-2 text-rose-600 dark:text-rose-300 font-medium">{r.message}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button type="button" onClick={reset} className="text-[9px] font-black uppercase tracking-widest text-indigo-500 hover:underline">Importar outro arquivo</button>
          </div>
        )}

        {parsed && summary && !results && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3 text-[11px] font-bold text-slate-600 dark:text-slate-300">
              <span className="px-3 py-1.5 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">{fileName}</span>
              <span>{summary.total} linha(s)</span>
              <span className="text-emerald-600">{summary.valid} válida(s)</span>
              {summary.invalid > 0 && <span className="text-rose-600">{summary.invalid} com erro</span>}
              {summary.duplicates > 0 && <span className="text-amber-600">{summary.duplicates} possível(is) duplicidade(s)</span>}
              <button type="button" onClick={reset} className="ml-auto flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-rose-500"><X size={12} /> Cancelar</button>
            </div>

            {/* Mapeamento de colunas */}
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">1. Associe cada coluna do arquivo a um campo do Allodos</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {parsed.headers.map((h, idx) => (
                  <div key={idx} className={`p-3 rounded-2xl border ${mapping[idx] ? 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700' : 'bg-amber-50/60 dark:bg-amber-950/10 border-amber-200 dark:border-amber-900/40'}`}>
                    <label className={label} htmlFor={`map-${idx}`}>Coluna "{h || `#${idx + 1}`}"</label>
                    <select id={`map-${idx}`} value={mapping[idx] ?? ''} onChange={(e) => changeMapping(idx, e.target.value as ImportField | '')} className={select}>
                      <option value="">Não importar</option>
                      {IMPORT_FIELDS.map((f) => <option key={f} value={f}>{IMPORT_FIELD_LABELS[f]}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              {!hasNameColumn && (
                <p className="mt-3 flex items-center gap-2 text-[11px] font-bold text-rose-600"><AlertTriangle size={14} /> Associe uma coluna ao campo "Nome completo" — ele é obrigatório.</p>
              )}
              {summary.unmappedColumns.length > 0 && (
                <p className="mt-3 text-[11px] font-bold text-amber-700 dark:text-amber-300">
                  Não serão importadas (sem campo correspondente no Allodos): {summary.unmappedColumns.map((c) => `"${c}"`).join(', ')}.
                </p>
              )}
            </div>

            {/* Padrões clínicos e vínculo */}
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">2. Padrões para os pacientes importados (ajustáveis depois na ficha)</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><label className={label} htmlFor="import-approach">Abordagem</label>
                  <select id="import-approach" value={approach} onChange={(e) => setApproach(e.target.value)} className={select}>{APPROACH_LIST.map((a) => <option key={a} value={a}>{a}</option>)}</select></div>
                <div><label className={label} htmlFor="import-frequency">Periodicidade do ciclo</label>
                  <select id="import-frequency" value={frequency} onChange={(e) => setFrequency(e.target.value)} className={select}>{Object.values(CycleFrequency).map((f) => <option key={f} value={f}>{f}</option>)}</select></div>
                <div><label className={label} htmlFor="import-professional">Profissional responsável</label>
                  {lockedToSelf ? (
                    <div className={`${select} bg-slate-100 dark:bg-slate-800`}>{activeProfile.name || 'Você'} (seu usuário)</div>
                  ) : (
                    <select id="import-professional" value={professionalId} onChange={(e) => setProfessionalId(e.target.value)} className={select}>
                      <option value="">Definir depois</option>
                      {professionals.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  )}
                </div>
              </div>
            </div>

            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Indicadores de acompanhamento iniciais (ao menos um)</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium mb-3">Os mesmos indicadores valem para todos os pacientes importados; ajuste depois em cada ficha.</p>
              <div className="space-y-3">
                {indicatorsByDomain.map(([domain, items]) => (
                  <div key={domain}>
                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-400 mb-1.5 ml-1">{domain}</p>
                    <div className="flex flex-wrap gap-2">
                      {items.map((ind) => {
                        const on = indicatorIds.includes(ind.id);
                        return (
                          <button key={ind.id} type="button" onClick={() => toggleIndicator(ind.id)} aria-pressed={on}
                            className={`px-3 py-1.5 rounded-full text-[10px] font-bold border transition-all ${on ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-300'}`}>
                            {ind.nome}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {indicatorIds.length === 0 && (
                <p className="mt-3 flex items-center gap-2 text-[11px] font-bold text-rose-600"><AlertTriangle size={14} /> Selecione ao menos um indicador.</p>
              )}
            </div>

            {/* Prévia */}
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">3. Prévia ({Math.min(previewRows.length, rows.length)} de {rows.length})</p>
              <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                <table className="w-full text-left text-[11px] min-w-[720px]">
                  <thead className="bg-slate-50 dark:bg-slate-800 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    <tr><th className="px-4 py-2">Linha</th><th className="px-4 py-2">Nome</th><th className="px-4 py-2">Nascimento</th><th className="px-4 py-2">Telefone</th><th className="px-4 py-2">E-mail</th><th className="px-4 py-2">Situação</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {previewRows.map((r) => (
                      <tr key={r.line}>
                        <td className="px-4 py-2 font-bold text-slate-400">{r.line}</td>
                        <td className="px-4 py-2 font-bold dark:text-white">{r.record.name || <span className="text-rose-500">—</span>}</td>
                        <td className="px-4 py-2 text-slate-500">{r.record.dob ? r.record.dob.split('-').reverse().join('/') : '—'}</td>
                        <td className="px-4 py-2 text-slate-500">{r.record.phone ?? '—'}</td>
                        <td className="px-4 py-2 text-slate-500">{r.record.email ?? '—'}</td>
                        <td className="px-4 py-2 font-medium">
                          {r.errors.length ? <span className="text-rose-600">{r.errors.join(' ')}</span>
                            : r.duplicateOf ? <span className="text-amber-700 dark:text-amber-300">Possível duplicidade: {r.duplicateOf.name} ({r.duplicateOf.reason})</span>
                            : r.duplicateInFile !== undefined ? <span className="text-amber-700 dark:text-amber-300">Repetido na linha {r.duplicateInFile}</span>
                            : <span className="text-emerald-600">Pronto para importar</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {summary.duplicates > 0 && (
                <label className="mt-3 flex items-start gap-3 text-[11px] font-bold text-slate-600 dark:text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={skipDuplicates} onChange={(e) => setSkipDuplicates(e.target.checked)} className="mt-0.5 rounded" />
                  <span>Não importar as possíveis duplicidades (recomendado). Desmarque para criar os registros mesmo assim — nenhum cadastro existente é sobrescrito.</span>
                </label>
              )}
            </div>

            {/* Confirmação */}
            {!confirming ? (
              <button type="button" disabled={!hasNameColumn || toImport.length === 0 || indicatorIds.length === 0} onClick={() => setConfirming(true)}
                className="px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-800">
                Revisar e confirmar ({toImport.length})
              </button>
            ) : (
              <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-900/50 space-y-3">
                <p className="text-sm font-black text-slate-800 dark:text-white">Confirmar importação de {toImport.length} paciente(s)?</p>
                <ul className="text-[11px] text-slate-600 dark:text-slate-300 font-medium list-disc ml-5 space-y-1">
                  <li>Destino: cadastro de Pacientes, abordagem "{approach}", periodicidade "{frequency}", {indicatorIds.length} indicador(es) inicial(is).</li>
                  <li>Profissional responsável: {lockedToSelf ? `${activeProfile.name || 'você'} (seu usuário)` : professionals.find((p) => p.id === professionalId)?.name || 'a definir depois'}.</li>
                  {summary.invalid > 0 && <li>{summary.invalid} linha(s) com erro ficam de fora e aparecem no relatório.</li>}
                  {summary.duplicates > 0 && <li>{skipDuplicates ? 'Possíveis duplicidades ficam de fora.' : 'Possíveis duplicidades serão criadas como novos cadastros.'}</li>}
                </ul>
                {running ? (
                  <p className="flex items-center gap-2 text-[11px] font-bold text-indigo-600"><Loader2 size={14} className="animate-spin" /> Importando {progress} de {toImport.length}...</p>
                ) : (
                  <div className="flex gap-3">
                    <button type="button" onClick={() => void run()} className="px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest bg-emerald-600 hover:bg-emerald-700 text-white">Confirmar importação</button>
                    <button type="button" onClick={() => setConfirming(false)} className="px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">Voltar</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default ImportData;
